import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useColorModeValue,
} from "@chakra-ui/react";
import { useField } from "formik";
import { useCallback, useLayoutEffect, useRef } from "react";

const LARGURA_INTERNA = 640;
const ALTURA_INTERNA = 240;

function canvasEstaEmBranco(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return true;
  }
  const { width, height } = canvas;
  if (width === 0 || height === 0) {
    return true;
  }
  const data = ctx.getImageData(0, 0, width, height).data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    if (r !== 255 || g !== 255 || b !== 255) {
      return false;
    }
  }
  return true;
}

type Props = {
  name?: string;
  label?: string;
};

const AssinaturaRecebedorField = ({
  name = "url_assinatura_recebedor",
  label = "Assinatura do recebedor",
}: Props) => {
  const [, { error, touched }, { setValue }] = useField(name);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const desenhando = useRef(false);
  const ultimo = useRef<{ x: number; y: number } | null>(null);

  const borderColor = useColorModeValue("gray.300", "gray.600");
  const fillBg = useColorModeValue("#ffffff", "#f7fafc");
  const strokeColor = useColorModeValue("#1a202c", "#171923");

  const pintarFundo = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }
    ctx.fillStyle = fillBg;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, [fillBg]);

  const sincronizarValor = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    if (canvasEstaEmBranco(canvas)) {
      setValue("");
    } else {
      setValue(canvas.toDataURL("image/png"));
    }
  }, [setValue]);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    canvas.width = LARGURA_INTERNA;
    canvas.height = ALTURA_INTERNA;
    pintarFundo();
  }, [pintarFundo, fillBg]);

  const posCanvas = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return { x: 0, y: 0 };
    }
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    desenhando.current = true;
    ultimo.current = posCanvas(e);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!desenhando.current) {
      return;
    }
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx || !ultimo.current) {
      return;
    }
    const p = posCanvas(e);
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(ultimo.current.x, ultimo.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    ultimo.current = p;
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (_) {}
    desenhando.current = false;
    ultimo.current = null;
    sincronizarValor();
  };

  const limpar = () => {
    pintarFundo();
    setValue("");
  };

  return (
    <FormControl isInvalid={!!(touched && error)} mb={4}>
      <FormLabel>
        <Flex mt={2} justifyContent="space-between">
        {label}
          <Button size="sm"  colorScheme="blackAlpha" onClick={limpar} type="button">
            Limpar assinatura
          </Button>
        </Flex>
        </FormLabel>
      <Box
        borderWidth="2px"
        borderColor={borderColor}
        borderRadius="md"
        overflow="hidden"
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "200px",
            display: "block",
            cursor: "crosshair",
            touchAction: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      </Box>
      {error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  );
};

export default AssinaturaRecebedorField;
